/* eslint-disable arrow-body-style */
import React, { useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PropTypes } from 'prop-types';
import * as ReactDOM from 'react-dom';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Box,
  Button,
  Container,
  Stepper,
  Step,
  StepLabel,
  Grid,
  CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import styled from '@mui/system/styled';

import { FooterContext } from '@seccon/core/contexts/footer';
import { FooterContainerWithLeftAside } from '@seccon/shared/ui/components/FooterContainer';
import Spinner from '@seccon/shared/ui/components/Spinner';
import { ToastContext } from '@seccon/core/contexts/toast';
import CSRFContext from '@seccon/core/contexts/csrf';

import DestinationsForm from './components/DestinationsForm';
import EmergencyContactsForm from './components/EmergencyContactsForm';
import EmployeeForm from './components/EmployeeForm';
import ForeignTravelOverviewForm from './components/ForeignTravelOverviewForm';
import IncidentForm from './components/IncidentInformation';
import PassportInformationForm from './components/PassportInformationForm';
import useCognizantOffice from './hooks/useCognizantOffice';
import useCurrentUserInfo from './hooks/useCurrentUserInfo';
import useForeignTravel from './hooks/useForeignTravel';
import useStatusOfIncident from './hooks/useIncidentStatus';
import useOtherPersonnelInCurrentFacility from './hooks/useOtherPersonnelInCurrentFacility';
import useModeOfTransportation from './hooks/useModeOfTransportation';
import useOtherFacilities from './hooks/useOtherFacilities';
import useRelationship from './hooks/useRelationship';
import useTravelReason from './hooks/useTravelReason';
import useUpdateReport from './hooks/useUpdateReport';
import { ACTION_UPDATE, EMPLOYEE_PORTAL, FACILITIES } from './constants';
import DocumentsForm from './components/DocumentsForm';

const LoadingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%'
});

const UpdateForeignTravel = () => {
  const incidentFormRef = useRef();
  const employFormRef = useRef();
  const destinationFormRef = useRef();
  const overviewFormRef = useRef();
  const passportFormRef = useRef();
  const emergencyFormRef = useRef();
  const documentFormRef = useRef();

  const { reportId } = useParams();
  const [searchParams] = useSearchParams();
  const origin = searchParams.get('origin');
  const incidentId = searchParams.get('incidentId');
  const [activeStep, setActiveStep] = useState(origin === FACILITIES ? 0 : 1);
  const [isValidating, setIsValidating] = useState(false);

  let destinationPage = '/employee/index';

  if (origin === FACILITIES) {
    destinationPage = incidentId ? `/incident/show/${ incidentId }` : '/incident/list';
  }

  const steps = origin === FACILITIES ? [
    'Incident',
    'Employee',
    'Destinations',
    'Overview',
    'Passport',
    'Emergency',
    'Document'
  ] : [
    'Employee',
    'Destinations',
    'Overview',
    'Passport',
    'Emergency',
    'Document'
  ];
  const methods = useForm({
    mode: 'all'
  });

  const { data, isLoading, isError } = useForeignTravel(reportId, origin, methods.reset);
  const initialDocuments = data && data.foreignTravel?.documents;

  const currentUserInfo = useCurrentUserInfo();
  const modeOfTransportation = useModeOfTransportation();
  const travelReason = useTravelReason();
  const relationship = useRelationship();

  const loadIncidentDropdownOptions = origin === FACILITIES;

  const statusOfIncident = useStatusOfIncident(loadIncidentDropdownOptions);
  const otherPersonnelInCurrentFacility = useOtherPersonnelInCurrentFacility(data?.incident?.id, loadIncidentDropdownOptions);
  const cognizantOffice = useCognizantOffice(loadIncidentDropdownOptions);
  const otherFacilities = useOtherFacilities(data?.incident?.id, loadIncidentDropdownOptions);

  const { updateReport, isSubmitting } = useUpdateReport(reportId, origin, destinationPage, initialDocuments, cognizantOffice);

  const handleCancel = () => {
    window.location = destinationPage;
  }

  const formNavigation = async (step) => {
    let isForm1Valid = true;
    switch (activeStep) {
      case 0:
        isForm1Valid = await incidentFormRef.current.validate();
        break;
      case 1:
        isForm1Valid = await employFormRef.current.validate();
        break;
      case 2:
        isForm1Valid = await destinationFormRef.current.validate();
        break;
      case 3:
        isForm1Valid = await overviewFormRef.current.validate();
        break;
      case 4:
        isForm1Valid = await passportFormRef.current.validate();
        break;
      case 5:
        isForm1Valid = await emergencyFormRef.current.validate();
        break;
      case 6:
        isForm1Valid = await documentFormRef.current?.validate() ? documentFormRef.current?.validate() : true;
        break;
      default:
        break;
    }

    if (isForm1Valid) {
      setActiveStep(step);
    }
  };
  const handleNext = async () => {
    formNavigation(activeStep + 1);
  };

  const handleBack = () => {
    formNavigation(activeStep - 1);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return <IncidentForm ref={incidentFormRef} statusOfIncident={statusOfIncident} otherPersonnelInCurrentFacility={otherPersonnelInCurrentFacility} cognizantOffice={cognizantOffice} otherFacilities={otherFacilities} />;
      case 1:
        return <EmployeeForm ref={employFormRef} initialData={currentUserInfo} action={ACTION_UPDATE} />;
      case 2:
        return <DestinationsForm ref={destinationFormRef} initialData={modeOfTransportation} />;
      case 3:
        return <ForeignTravelOverviewForm ref={overviewFormRef} travelReason={travelReason} modeOfTransportation={modeOfTransportation} />;
      case 4:
        return <PassportInformationForm ref={passportFormRef} />;
      case 5:
        return <EmergencyContactsForm ref={emergencyFormRef} initialData={relationship} />;
      case 6:
        return <DocumentsForm ref={documentFormRef} setIsValidating={setIsValidating} />;
      default:
        return 'Unknown step';
    }
  };

  const { isValid } = methods.formState;

  const pointerStyle = {
    cursor: 'pointer'
  }
  return (
    <FormProvider {...methods}>
      {isLoading || isError ? (
        <LoadingContainer>
          {!isError && <CircularProgress size={100} />}
        </LoadingContainer>
      ) : (
        <Container>
          { /* activeStep is off by one when viewing from EMPLOYEE_PORTAL because the activeStep starts at 1 but the steps outputted omits the Incident Step */ }
          <Stepper sx={{ m: 4 }} activeStep={activeStep - (origin === FACILITIES ? 0 : 1)} alternativeLabel>
            {steps.map((label, key) => (
              <Step key={label}>
                <StepLabel style={pointerStyle} onClick={() => formNavigation(key)}> { label } </StepLabel>
              </Step>
            ))}
          </Stepper>
          <Container sx={{ paddingBottom: '2rem' }}>
            {getStepContent(activeStep)}
          </Container>
        </Container>
      )}
      <Footer
        activeStep={activeStep}
        destinationPage={destinationPage}
        isSubmitting={isSubmitting}
        isLoading={isLoading}
        isError={isError}
        isValid={isValid}
        isValidating={isValidating}
        onBack={handleBack}
        onNext={handleNext}
        onCancel={handleCancel}
        onSubmit={methods.handleSubmit(updateReport)}
        origin={origin}
        reportId={reportId}
      />
    </FormProvider>
  );
};

const Footer = ({
  activeStep,
  destinationPage,
  isSubmitting,
  isLoading,
  isError,
  isValid,
  isValidating,
  onBack,
  onNext,
  onCancel,
  onSubmit,
  origin,
  reportId
}) => {
  const toastApi = React.useContext(ToastContext);
  const footerEl = React.useContext(FooterContext);
  const csrfToken = React.useContext(CSRFContext);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  // If the footer is not defined, do nothing
  if (footerEl === null) {
    return null;
  }

  const handleCancelForeignTravel = () => {
    setOpenConfirmModal(true);
  };

  const handleConfirmCancel = async (foreignTravelId) => {
    setIsCancelling(true);
    setOpenConfirmModal(false);
    try {
      const response = await fetch(`/foreignTravel/${ foreignTravelId }/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'anti-csrf-token': csrfToken
        }
      });
      if (response.ok) {
        toastApi.success('Successfully cancelled foreign travel report');
        // Redirect after success
        setTimeout(() => {
          window.location.href = destinationPage;
        }, 3000);
      } else {
        const errorData = await response.json();
        toastApi.error(`Unable to cancel foreign travel report: ${ errorData.message || response.statusText }`);
      }
    } catch (error) {
      toastApi.error('Unable to cancel foreign travel report');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCloseModal = () => {
    setOpenConfirmModal(false);
  };

  const isFirstStep = origin === FACILITIES ? activeStep === 0 : activeStep === 1;
  const isLastStep = activeStep === 6;

  const content = (
    <FooterContainerWithLeftAside id='foreignTravelReportFooter'>
      <Grid container justifyContent='space-between' alignItems='center'>
        <Grid item>
          <Grid container spacing={2}>
            <Grid item>
              <Button variant='outlined' onClick={onBack} disabled={isFirstStep || isLoading}>
                PREVIOUS
              </Button>
            </Grid>
            <Grid item>
              <Button variant='outlined' color='primary' onClick={onNext} disabled={isLastStep || isLoading || isError}>
                NEXT
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid container spacing={2}>
            {origin === EMPLOYEE_PORTAL && (
              <Grid item>
                <Button variant='outlined' color='error' onClick={handleCancelForeignTravel} disabled={isCancelling || isLoading || isError}>
                  {isCancelling ? <CircularProgress size={24} /> : 'CANCEL REPORT'}
                </Button>
              </Grid>
            )}
            <Grid item>
              <Button variant='outlined' color='primary' onClick={onCancel}>
                CANCEL
              </Button>
            </Grid>
            <Grid item>
              <Button variant='contained' color='primary' onClick={onSubmit} disabled={isSubmitting || isLoading || isError || !isValid || isValidating} sx={{ minWidth: '85px', minHeight: '36px' }}>
                {isSubmitting ? <Spinner /> : 'UPDATE'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* MUI Confirmation Modal */}
      <Dialog open={openConfirmModal} onClose={handleCloseModal}>
        <DialogTitle>Cancel Foreign Travel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel foreign travel report? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color='primary'>
            No
          </Button>
          <Button onClick={() => handleConfirmCancel(reportId)} color='primary' autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </FooterContainerWithLeftAside>
  );

  return ReactDOM.createPortal(content, footerEl);
};

Footer.propTypes = {
  activeStep: PropTypes.number.isRequired,
  destinationPage: PropTypes.string.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  isValidating: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  origin: PropTypes.string.isRequired,
  reportId: PropTypes.string.isRequired
};

export default UpdateForeignTravel;
